import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import ApplicantForm from "../Forms/check/ApplicationForm";
import { validateField } from "../../utils/validation/validation";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import {Row, Button} from "react-bootstrap";
import InputField from "../../utils/InputField";
import TextAreaField from "../../utils/TextAreaField";
import { updateApplication } from "../../services/check-api";
import { useToast } from '../../contexts/ToastContext';
import { useLoading } from "../../contexts/LoadingContext";
import { formatDate } from "../../utils/TableUtils";
import { formatPhoneNumber } from "../../utils/phoneFormatter";
import Alert from "react-bootstrap/Alert";
import { fetchSignatories } from "../../services/signatoryService";
import { useAuth } from "../../contexts/AuthContext";
import { useParams } from "react-router-dom";


// The Lakeside team, used on the global admin application lists. Inside one
// community's dashboard - and for community logins - that community's own
// signatories replace this list.
const LC_REVIEWERS = ["Rick Dandan", "Gene Lode", "John Fjellander"];

function CheckApplicationModal({ show, onHide, data, isPortalUser = false, onSave, communityId = null }) {
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const { role } = useAuth();
  // An admin working inside one community's dashboard (/admin/communities/:id)
  // is acting on that community's behalf, so the document is signed by their
  // people. The global application lists keep the LC team. The id is handed
  // down by the tab page, which already reads it; the route param is a
  // fallback for any caller that doesn't pass it.
  const { id: routeCommunityId } = useParams();
  const isCommunity = role === "Community";
  const adminCommunityId = isCommunity ? null : (communityId || routeCommunityId || null);
  const signsAsCommunity = isCommunity || !!adminCommunityId;
  
  // Format phone number if it exists and is not already formatted
  const formatPhoneIfNeeded = (phone) => {
    if (!phone) return "";
    if (phone.includes('-')) return phone; // Already formatted
    return formatPhoneNumber(phone);
  };
  
  const initialFormValue = {
    applicant: {
      role: data?.role || "",
      name: data?.name || "",
      address: data?.address || "",
      phoneNumber: formatPhoneIfNeeded(data?.phoneNumber),
      email: data?.email || "",
      communityEmailAddress: data?.communityEmailAddress || "",
      planReviewType: data?.planReviewType || "",
      comments: data?.comments || "",
      files: data?.files || [],
      fileLinks: data?.fileLinks || "",
      createdAt: formatDate(data?.createdAt) || "",
      updatedAt: formatDate(data?.updatedAt) || "",
    },
    app: {
       status: data?.status || "Pending",
       reason: data?.reason || "",
       reviewerNotes: data?.reviewerNotes || "",
       reviewerName: data?.reviewerName || "",
       signatoryId: "",
    }
   
  };
  const [formData, setFormData] = useState(initialFormValue);
  const [errors, setErrors] = useState({
    applicant: {}
  });

  // Fetched only when signing as a community; the LC list needs no request.
  const [signatories, setSignatories] = useState([]);
  const [signatoriesLoaded, setSignatoriesLoaded] = useState(false);

  useEffect(() => {
    if (isPortalUser || !signsAsCommunity) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const list = await fetchSignatories({ communityId: adminCommunityId, product: "check" });
        if (cancelled) return;
        setSignatories(list);

        // Re-select whoever signed last time, else fall back to the default.
        const preset =
          list.find((s) => s.fullName === data?.reviewerName) ||
          (data?.reviewerName ? null : list.find((s) => s.isDefault));

        if (preset) {
          setFormData((prev) => ({
            ...prev,
            app: {
              ...prev.app,
              signatoryId: String(preset.id),
              reviewerName: preset.fullName,
            },
          }));
        }
      } catch (err) {
        if (!cancelled) setSignatories([]);
      } finally {
        if (!cancelled) setSignatoriesLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isPortalUser, signsAsCommunity, adminCommunityId, data?.reviewerName]);

  const handleSignatoryChange = (field, value) => {
    const chosen = signatories.find((s) => String(s.id) === String(value));
    setFormData((prev) => ({
      ...prev,
      app: {
        ...prev.app,
        signatoryId: value,
        reviewerName: chosen ? chosen.fullName : "",
      },
    }));
  };
  const handleDataChange = (section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: { ...prevData[section], [field]: value },
    }));
  };
  const handleValidate = (section, field, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [section]: { ...prevErrors[section], [field]: error },
    }));
  };

  const validateEntireForm = () => {
    const applicantErrors = {};

    let isValid = true;

    Object.entries(formData.applicant).forEach(([field, value]) => {
      const error = validateField(field, value);
      if (error) {
        applicantErrors[field] = error;
        isValid = false;
      }
    });
    setErrors({ applicant: applicantErrors });
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateEntireForm();
    if (!isValid) return;

    if (signsAsCommunity && !isPortalUser && formData.app.status === "Approved" && !formData.app.reviewerName) {
      showToast("Choose who is signing this review before approving it.", "error");
      return;
    }
    try {
      startLoading();
      const combinedData = {
        ...formData.applicant,
        ...formData.app,
      };
      if (isPortalUser && onSave) {
        await onSave(combinedData);
      } else {
        await updateApplication(data.id, combinedData);
      }
      showToast('Update successfully!', 'success');
      onHide();
    } catch (error) {
      showToast(error.message || "Update failed. Please try again.", 'error');
    } finally {
      stopLoading();
    }
  };


  const applicationStatus = data ? data.status : "Pending";
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="add-community-modal"
      className="add-community-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-uppercase" id="add-community-modal">
          Edit Application
        </Modal.Title>
        {data && (
          <div
            className={`modal-header-status status-${applicationStatus.toLowerCase().replace(/\s+/g, '-')}`}
          >
            Status:{" "}
            <span className={`span-${applicationStatus.toLowerCase().replace(/\s+/g, '-')}`}>
              {applicationStatus}
            </span>
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <div className="form-wrapper mb-5 form-modal">
            <Row>
              {data && !isPortalUser && (
                <Col md="4">
                  <Row className="mb-3">
                    <InputField
                      field="status"
                      label="Status"
                      value={formData.app.status}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      error={errors.status}
                      type="select"
                      options={["Approved", "Not Approved"]}
                      as={Col}
                      controlId="applicantStatus"
                    />
                  </Row>
                  <Row className="mb-3">
                    {signsAsCommunity ? (
                      <InputField
                        field="signatoryId"
                        label="Reviewer Name"
                        value={formData.app.signatoryId}
                        onChange={handleSignatoryChange}
                        error={errors.reviewerName}
                        type="select"
                        options={signatories.map((s) => ({
                          value: String(s.id),
                          label: s.fullName,
                        }))}
                        as={Col}
                        controlId="reviewerName"
                      />
                    ) : (
                      <InputField
                        field="reviewerName"
                        label="Reviewer Name"
                        value={formData.app.reviewerName}
                        onChange={(field, value) =>
                          handleDataChange("app", field, value)
                        }
                        error={errors.reviewerName}
                        type="select"
                        options={LC_REVIEWERS}
                        as={Col}
                        controlId="reviewerName"
                      />
                    )}
                  </Row>
                  {signsAsCommunity && signatoriesLoaded && signatories.length === 0 && (
                    <Row className="mb-3">
                      <Col>
                        <Alert variant="warning" className="py-2 px-3 small mb-0">
                          {isCommunity
                            ? "You haven't added any signatures yet. Add one under Signatures in the left menu before approving."
                            : "This community hasn't added any signatures yet, so there is nobody to sign the review."}
                        </Alert>
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-3">
                    <TextAreaField
                      field="reason"
                      label="Comments"
                      rows={4}
                      value={formData.app.reason}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      error={errors.reason}
                      as={Col}
                      controlId="applicantComments"
                      placeholder=""
                    />
                  </Row>
                  <Row className="mb-3">
                    <TextAreaField
                      field="reviewerNotes"
                      label="Reviewer Notes"
                      rows={4}
                      value={formData.app.reviewerNotes}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      error={errors.reviewerNotes}
                      as={Col}
                      controlId="reviewerNotes"
                      placeholder=""
                    />
                  </Row>
                  {formData.applicant.comments && (
                    <Row className="mb-3">
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-uppercase fw-bold">Comment Area</Form.Label>
                          <div
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              padding: '0.75rem',
                              minHeight: '80px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              color: '#495057',
                              fontSize: '0.875rem',
                            }}
                          >
                            {formData.applicant.comments}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                </Col>
              )}
              <Col md={isPortalUser ? "12" : "8"}>
              <ApplicantForm
                      data={formData.applicant}
                      onChange={(field, value) =>
                        handleDataChange("applicant", field, value)
                      }
                      onValidate={(field, error) =>
                        handleValidate("applicant", field, error)
                      }
                      errors={errors.applicant || {}}
                      isFrom={"modal"}
                    />
              </Col>
            </Row>
          </div>
          <div className="text-end">
            <Button type="submit" className="btn btn-submit">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default CheckApplicationModal;

